import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Plus, Check, XCircle, Eye, EyeOff } from "lucide-react";
import { colors, type, spacing, radius } from "@/theme";
import { api } from "@/lib/api";
import { useRequireAuth } from "@/lib/useRequireAuth";

export default function AdminTables() {
  const navigate = useNavigate();
  const { user } = useRequireAuth();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState("");
  const [bulkTo, setBulkTo] = useState("");
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState("");

  const load = useCallback(async () => { const d = await api.adminTables(); setTables(d.tables); setLoading(false); }, []);
  useEffect(() => { if (user) load(); }, [user, load]);
  if (!user) return null;

  const add = async () => { if (!newLabel.trim()) return; await api.createTable(newLabel.trim()); setNewLabel(""); load(); };
  const bulk = async () => { const n = parseInt(bulkTo); if (!n) return; await api.bulkTables(1, n); setBulkTo(""); load(); };
  const toggle = async (t) => { await api.updateTable(t.id, { attivo: !t.attivo }); load(); };
  const closeSession = async (t) => { await api.closeTableSession(t.id); load(); };
  const saveRename = async (t) => { if (editVal.trim()) await api.updateTable(t.id, { etichetta: editVal.trim() }); setEditing(null); load(); };

  return (
    <div style={{ minHeight: "100vh", background: colors.bg }}>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <div style={head}>
          <button data-testid="back-button" onClick={() => navigate(-1)} style={plain}><ChevronLeft size={26} color={colors.ink} /></button>
          <div style={{ ...type.serif, fontSize: 22, color: colors.ink }}>Tavoli</div>
          <div style={{ width: 26 }} />
        </div>

        <div style={{ display: "flex", gap: spacing.sm, padding: `0 ${spacing.lg}px`, marginBottom: spacing.sm }}>
          <input data-testid="new-table-input" style={{ ...input, flex: 1 }} value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder='Etichetta (es. "12", "Dehors 3")' onKeyDown={(e) => e.key === "Enter" && add()} />
          <button data-testid="add-table-button" onClick={add} style={{ width: 48, height: 48, borderRadius: radius.md, background: colors.accent, border: "none", display: "flex", alignItems: "center", justifyContent: "center" }}><Plus size={20} color={colors.onAccent} /></button>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: spacing.sm, padding: `0 ${spacing.lg}px`, marginBottom: spacing.sm }}>
          <span style={{ ...type.sans, fontSize: 14, color: colors.inkSoft }}>Crea in blocco da 1 a</span>
          <input data-testid="bulk-to-input" style={{ ...input, width: 64, height: 40, textAlign: "center" }} value={bulkTo} onChange={(e) => setBulkTo(e.target.value)} inputMode="numeric" placeholder="30" />
          <button data-testid="bulk-create-button" onClick={bulk} style={{ background: colors.surfaceTertiary, borderRadius: radius.md, padding: `0 ${spacing.md}px`, height: 40, border: "none", ...type.sansSemi, fontSize: 14, color: colors.ink }}>Crea</button>
        </div>

        {loading ? <div style={{ padding: spacing.xl, textAlign: "center", ...type.sans, color: colors.accent }}>Caricamento…</div> : (
          <div style={{ padding: spacing.lg, display: "flex", flexDirection: "column", gap: spacing.sm }}>
            {tables.map((t) => (
              <div key={t.id} data-testid={`table-row-${t.etichetta}`} style={{ display: "flex", alignItems: "center", gap: spacing.sm, background: colors.surface, borderRadius: radius.md, padding: spacing.md, border: `1px solid ${colors.line}`, opacity: t.attivo ? 1 : 0.5 }}>
                {editing === t.id ? (
                  <input data-testid={`rename-input-${t.id}`} style={{ ...input, flex: 1 }} value={editVal} onChange={(e) => setEditVal(e.target.value)} autoFocus onKeyDown={(e) => e.key === "Enter" && saveRename(t)} />
                ) : (
                  <button style={{ flex: 1, background: "none", border: "none", textAlign: "left", padding: 0 }} onClick={() => { setEditing(t.id); setEditVal(t.etichetta); }} data-testid={`table-label-${t.etichetta}`}>
                    <div style={{ ...type.serif, fontSize: 18, color: colors.ink }}>{t.etichetta}</div>
                    {t.session_aperta ? <div style={{ ...type.sansMedium, fontSize: 12, color: colors.accent, marginTop: 2 }}>Sessione aperta · codice {t.codice}</div> : <div style={{ ...type.sans, fontSize: 12, color: colors.inkSoft, marginTop: 2 }}>Libero</div>}
                  </button>
                )}
                {editing === t.id ? (
                  <button data-testid={`rename-save-${t.id}`} onClick={() => saveRename(t)} style={iconBtn}><Check size={18} color={colors.success} /></button>
                ) : (
                  <>
                    {t.session_aperta ? <button data-testid={`close-session-${t.etichetta}`} onClick={() => closeSession(t)} style={iconBtn}><XCircle size={18} color={colors.danger} /></button> : null}
                    <button data-testid={`toggle-active-${t.etichetta}`} onClick={() => toggle(t)} style={iconBtn}>{t.attivo ? <Eye size={18} color={colors.inkSoft} /> : <EyeOff size={18} color={colors.inkSoft} />}</button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const head = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${spacing.md}px ${spacing.lg}px` };
const plain = { background: "none", border: "none", display: "flex", padding: 0 };
const input = { background: colors.surface, border: `1px solid ${colors.line}`, borderRadius: radius.md, height: 48, padding: `0 ${spacing.md}px`, ...type.sans, fontSize: 15, color: colors.ink };
const iconBtn = { width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", background: "none", border: "none" };
